export type BeatLineCheckContext = {
  state: Record<string, any>;
  currentPlaylistId?: string;
  songMetaCache?: Record<string, any>;
  ajaxPost?: (action: string, payload?: Record<string, any>) => Promise<any>;
  beatlineBuildPlaylistParams?: (ids: unknown[]) => Record<string, any>;
  t?: (key: string, fallback?: string) => string;
  alert?: (message: string) => void;
  stopMusic?: () => void;
  beatlineSetDragEnabled?: (enabled: boolean) => void;
  beatlineQrSessionStop?: () => void;
  getOfflineChallengers?: () => number[];
  openInterventionDecision?: () => void;
  revealCardElement?: (el: Element, song: Record<string, any>) => void;
  finalizeTurn?: (playerId: number, valid: boolean) => void;
  perfectOrderCheck?: () => Promise<void> | void;
  endSurvival?: () => void;
  endTimeAttack?: () => void;
  getTimeAttackRemainingMs?: () => number;
};

export type BeatLineCheckResult = { handled: boolean; reason?: string };

function safeCall(fn: unknown, ...args: any[]): void {
  if (typeof fn !== 'function') return;
  try { fn(...args); } catch (_) {}
}

function getCardsForTurn(turnSlot: number): Element[] {
  const pZone = document.getElementById(`timeline-p${turnSlot}`);
  return Array.from((pZone && pZone.children) ? pZone.children : []);
}

function getT(ctx: BeatLineCheckContext) {
  return typeof ctx.t === 'function' ? ctx.t : ((key: string, fallback = key) => fallback);
}

function buildPlaylistPayload(ctx: BeatLineCheckContext): Record<string, any> {
  const state = ctx.state;
  const ids = state.playlistIds || [state.playlistId || ctx.currentPlaylistId];
  if (typeof ctx.beatlineBuildPlaylistParams === 'function') return ctx.beatlineBuildPlaylistParams(ids);
  return { playlist_ids: Array.isArray(ids) ? ids : [ids] };
}

/**
 * C7: TypeScript-owned normal local CHECK path.
 * Special solo/online/perfect paths stay in the golden legacy runtime for now.
 */
export async function initiateCheckFromTypeScript(ctx: BeatLineCheckContext): Promise<BeatLineCheckResult> {
  const { state } = ctx;
  if (!state) return { handled: false, reason: 'missing-state' };

  if (state.gameEnded) return { handled: true, reason: 'game-ended' };

  if (state.perfectOrder) {
    // Perfect order checks a full level/timeline and keeps its exact legacy handling.
    return { handled: false, reason: 'perfect-order' };
  }

  if (state.survival || state.timeAttack || state.speedrun) {
    return { handled: false, reason: 'complex-mode' };
  }

  const t = getT(ctx);
  const turnSlot = parseInt(String(state.turn || 1), 10) || 1;
  const cards = getCardsForTurn(turnSlot);
  const placedCard = cards.find((c) => c.classList.contains('card') && !c.classList.contains('revealed')) as HTMLElement | undefined;

  if (!placedCard) {
    if (typeof ctx.alert === 'function') ctx.alert(t('alert_place_card_first'));
    else window.alert(t('alert_place_card_first'));
    return { handled: true, reason: 'missing-card' };
  }

  safeCall(ctx.stopMusic);
  safeCall(ctx.beatlineQrSessionStop);
  safeCall(ctx.beatlineSetDragEnabled, false);

  const btnCheck = document.getElementById('btn-check') as HTMLButtonElement | null;
  if (btnCheck) btnCheck.disabled = true;

  const actualCards = cards.filter((c) => c.classList.contains('card')) as HTMLElement[];
  const realIndex = actualCards.indexOf(placedCard);
  const prevEl = realIndex > 0 ? actualCards[realIndex - 1] : null;
  const nextEl = realIndex < actualCards.length - 1 ? actualCards[realIndex + 1] : null;

  const activeId = placedCard.dataset.id || '';
  const prevId = prevEl ? (prevEl.dataset.id || '') : '';
  const nextId = nextEl ? (nextEl.dataset.id || '') : '';

  state.tempActivePlacement = {
    index: realIndex,
    valid: false,
    card: placedCard,
    activeId,
    prevId,
    nextId,
  };

  const challengers = typeof ctx.getOfflineChallengers === 'function' ? ctx.getOfflineChallengers() : [];
  const ajaxPost = ctx.ajaxPost;
  if (typeof ajaxPost !== 'function') return { handled: false, reason: 'missing-ajax' };

  const basePayload = Object.assign(buildPlaylistPayload(ctx), {
    mode: state.mode || 'spotify',
    active_id: activeId,
    prev_id: prevId,
    next_id: nextId,
  });

  if (challengers.length > 0) {
    const resp = await ajaxPost('beatline_validate_placement', basePayload);
    const valid = !!(resp && resp.success && resp.data && resp.data.valid);
    state.tempActivePlacement.valid = valid;
    state.__interventionChallengers = challengers;
    state.__interventionNoText = t('intervention_no_check', 'NEE (Gewoon Checken)');
    safeCall(ctx.openInterventionDecision);
    return { handled: true, reason: 'intervention-decision' };
  }

  const resp = await ajaxPost('beatline_validate_and_reveal', basePayload);
  const valid = !!(resp && resp.success && resp.data && resp.data.valid);
  const song = resp && resp.success && resp.data && resp.data.song ? resp.data.song : null;

  if (song && song.id) {
    if (ctx.songMetaCache) ctx.songMetaCache[song.id] = song;
    state.activeCard = song;
    safeCall(ctx.revealCardElement, placedCard, song);
  }

  state.tempActivePlacement.valid = valid;
  safeCall(ctx.finalizeTurn, turnSlot, valid);

  return { handled: true, reason: 'validate-and-reveal' };
}
